import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Params, Router } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, Observable, Subscription, take } from 'rxjs';
import { Store } from '@ngrx/store';

import { RootPaths } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { FinnairAirCalendarChangeListResponse, FinnairOrder } from '@fcom/dapi/api/models';
import {
  ButtonLayout,
  ButtonMode,
  ButtonPosition,
  ButtonSize,
  ButtonTheme,
  IconPosition,
  LoaderTheme,
  ModalButtons,
  NotificationLayout,
} from '@fcom/ui-components';
import { unsubscribe } from '@fcom/core/utils';
import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common';
import { VoluntaryChangeService } from '@fcom/voluntary-change/services/voluntary-change.service';
import { VOLUNTARY_CHANGE_STEPS } from '@fcom/voluntary-change/voluntary-change-config';
import { orderData, getChangeableBoundIds } from '@fcom/common-booking';
import { AppState } from '@fcom/core/interfaces/app-state.interface';
import { Amount } from '@fcom/dapi';

import { getChangeList, getChangeBoundIds } from '../../../../store/selectors/change.selector';
import { getTrackingDescription } from '../../../../utils';

interface BoundAndDate {
  bound: string;
  date: string;
}

@Component({
  selector: 'fin-mmb-select-matrix',
  templateUrl: './select-matrix.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingSelectMatrixComponent implements OnInit, OnDestroy {
  readonly ButtonLayout = ButtonLayout;
  readonly ButtonMode = ButtonMode;
  readonly ButtonPosition = ButtonPosition;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly LoaderTheme = LoaderTheme;
  readonly ModalButtons: typeof ModalButtons = ModalButtons;
  readonly NotificationLayout = NotificationLayout;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  booking$: Observable<FinnairOrder>;

  changeableBoundIds$: Observable<string[]>;
  selectedBoundsForChange$: Observable<string[]>;

  calendarChangeList$: Observable<FinnairAirCalendarChangeListResponse>;
  newDates$: BehaviorSubject<[string, string]> = new BehaviorSubject<[string, string]>(null);
  subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<AppState>,
    private router: Router,
    private lang: LanguageService,
    private gtmService: GtmService,
    private voluntaryChangeService: VoluntaryChangeService
  ) {}

  ngOnInit(): void {
    this.booking$ = this.store$.pipe(orderData());
    this.changeableBoundIds$ = this.store$.pipe(getChangeableBoundIds());

    this.selectedBoundsForChange$ = this.store$.pipe(getChangeBoundIds());

    this.calendarChangeList$ = this.store$.pipe(getChangeList());
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  selectDatesStep(): void {
    this.voluntaryChangeService.setStep({ step: VOLUNTARY_CHANGE_STEPS.SELECT_DATES });
  }

  closeModal(): void {
    this.subscriptions.add(
      combineLatest([this.booking$, this.changeableBoundIds$])
        .pipe(take(1))
        .subscribe(([booking, changeableBoundIds]) => {
          this.gtmService.trackElement(
            'view-pnr_change_abort-selectmatrix',
            GaContext.MANAGE_BOOKING,
            ElementTypes.MODAL,
            `recLoc-${booking.id}`,
            ElementActions.CLOSE
          );
          if (changeableBoundIds.length === 1) {
            this.voluntaryChangeService.goToViewPNR();
          } else {
            this.voluntaryChangeService.setStep({ step: VOLUNTARY_CHANGE_STEPS.SELECT_BOUNDS });
          }
        })
    );
  }

  redirectToChangeFlow(): void {
    this.subscriptions.add(
      combineLatest([this.booking$, this.newDates$, this.changeableBoundIds$, this.selectedBoundsForChange$])
        .pipe(take(1))
        .subscribe(([booking, newDates, changeableBoundIds, selectedBoundsForChange]) => {
          this.gtmService.trackElement(
            `view-pnr_change_matrix-${getTrackingDescription(changeableBoundIds, selectedBoundsForChange)}`,
            GaContext.MANAGE_BOOKING,
            ElementTypes.BUTTON,
            undefined,
            ElementActions.CLICK,
            `recLoc-${booking?.otherInformation.analyticsToken}`
          );

          this.voluntaryChangeService.setStep({ step: VOLUNTARY_CHANGE_STEPS.FLIGHT_SELECTION, skipRouteChange: true });
          this.router.navigate(
            [
              '/',
              this.lang.langValue || 'en',
              ...RootPaths.VOLUNTARY_CHANGE_ROOT.split('/'),
              VOLUNTARY_CHANGE_STEPS.FLIGHT_SELECTION.path,
            ],
            {
              queryParams: this.createChangeFlowTicketSelectionQueryParams(
                booking,
                this.boundsWithDates(newDates, selectedBoundsForChange)
              ),
            }
          );
        })
    );
  }

  boundsWithDates(newDates: [string, string], selectedBoundsForChange: string[]): BoundAndDate[] {
    return selectedBoundsForChange
      .filter(Boolean)
      .map((bound, index) => ({ bound, date: newDates.filter(Boolean)[index] }));
  }

  datesSelected({ dates }: { dates: [string, string]; price?: Amount }): void {
    this.newDates$.next(dates);
  }

  private createChangeFlowTicketSelectionQueryParams(booking: FinnairOrder, boundsWithDates: BoundAndDate[]): Params {
    if (boundsWithDates.length === 2) {
      return {
        ticketSelectionPhase: '0',
        departureBoundId: boundsWithDates[0].bound,
        departureNewDate: boundsWithDates[0].date,
        returnBoundId: boundsWithDates[1].bound,
        returnNewDate: boundsWithDates[1].date,
        directFlights: false,
      };
    }
    const indexOfFirstBound = booking.bounds.findIndex((bound) => bound.id === boundsWithDates[0].bound);
    return {
      directFlights: false,
      ticketSelectionPhase: indexOfFirstBound.toString(),
      ...(indexOfFirstBound === 0
        ? {
            departureBoundId: boundsWithDates[0].bound,
            departureNewDate: boundsWithDates[0].date,
          }
        : {
            returnBoundId: boundsWithDates[0].bound,
            returnNewDate: boundsWithDates[0].date,
          }),
    };
  }
}
